import React, { useEffect, useState } from 'react';
import {
  Alert,
  Container,
  TextField,
  Button,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  Autocomplete,
  MenuItem,
  Box,
  Divider,
  IconButton,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CurrencyInput from '../commons/CurrencyInput';
import { getCurrencyTypes } from '../../utils/Currency';
import walletApi, { createCorrelation } from '../../api/wallet';
import { formatValue } from '../../utils/utils';
import { getErrorMessage } from '../../utils/errors';

const emptyChildExpense = () => ({
  id: Date.now(),
  description: '',
  category: null,
  value: 0,
  isMandatory: false,
});

const CashOutForm = ({ onCreate }) => {
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currency, setCurrency] = useState('BRL');
  const [amount, setAmount] = useState(0);
  const [debitDate, setDebitDate] = useState(dayjs().locale('pt-br').format('DD/MM/YYYY'));
  const [observation, setObservation] = useState('');
  const [isMandatory, setIsMandatory] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const Currencytypes = getCurrencyTypes();

  // Child expenses state - add detailed expenses to this parent
  const [isConsolidated, setIsConsolidated] = useState(false);
  const [childExpenses, setChildExpenses] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await walletApi.get('/category?category_type=cash_out')
        setCategories(response.data.Categories);
      } catch (err) {
        setError(getErrorMessage(err, 'Falha ao carregar categorias.'));
      }
    };

    fetchCategories();
  }, []);

  // Child expense handlers
  const addChildExpense = () => {
    setChildExpenses([...childExpenses, emptyChildExpense()]);
  };

  const removeChildExpense = (id) => {
    setChildExpenses(childExpenses.filter(child => child.id !== id));
  };

  const updateChildExpense = (id, field, value) => {
    setChildExpenses(childExpenses.map(child =>
      child.id === id ? { ...child, [field]: value } : child
    ));
  };

  const getChildrenTotal = () => {
    return childExpenses.reduce((sum, child) => sum + (child.value || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Create the main expense
      const response = await walletApi.post('/cashout',
        {
          Description: description,
          CategoryId: selectedCategory ? selectedCategory.ID : null,
          IsMandatory: isMandatory,
          Value: String(amount),
          DebitDate: debitDate,
          Currency: currency,
          Observation: observation,
        });

      const parentId = response.data?.ID;
      let linkedCount = 0;

      // Create and link child expenses if this is a consolidated expense
      if (parentId && isConsolidated && childExpenses.length > 0) {
        for (const child of childExpenses) {
          if (!child.description || !child.category || !child.value) continue;

          try {
            // Create child expense
            const childResponse = await walletApi.post('/cashout', {
              Description: child.description,
              CategoryId: child.category.ID,
              IsMandatory: child.isMandatory,
              Value: String(child.value),
              DebitDate: debitDate,
              Currency: currency,
              Observation: `Parte de: ${description}`,
            });

            const childId = childResponse.data?.ID;

            // Link to parent
            if (childId) {
              await createCorrelation(parentId, childId);
              linkedCount++;
            }
          } catch (err) {
            console.error('Failed to create/link child expense:', err);
          }
        }
      }

      const childMsg = linkedCount > 0
        ? ` com ${linkedCount} despesa(s) detalhada(s)`
        : '';
      setSuccess(`Lançamento de custo criado com sucesso${childMsg}!`);

      // Reset form
      setDescription('');
      setSelectedCategory(null);
      setCurrency('BRL');
      setAmount(0);
      setObservation('');
      setIsMandatory(false);
      setIsConsolidated(false);
      setChildExpenses([]);

      if (onCreate) onCreate();
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao criar o lançamento de custo.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          mb: 4,
        }}
      >
        <Typography variant="h4" gutterBottom align="center">
          Novo Custo
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <DatePicker
              label="Data de Débito"
              value={dayjs(debitDate, 'DD/MM/YYYY')}
              onChange={(newValue) => setDebitDate(newValue.format('DD/MM/YYYY'))}
              format="DD/MM/YYYY"
              slots={{
                textField: (params) => <TextField {...params} fullWidth margin="normal" />
              }}
            />
          </LocalizationProvider>
          <TextField
            label="Descrição"
            variant="outlined"
            margin="normal"
            fullWidth
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option.Name}
            value={selectedCategory}
            onChange={(event, newValue) => {
              setSelectedCategory(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Categoria"
                variant="outlined"
                margin="normal"
                fullWidth
                required
              />
            )}
          />
          <FormControlLabel
            control={<Switch checked={isMandatory} onChange={(e) => setIsMandatory(e.target.checked)} />}
            label="É obrigatório?"
          />
          <TextField
            select
            label="Moeda"
            variant="outlined"
            margin="normal"
            fullWidth
            required
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            {Currencytypes.map((typeOption) => (
              <MenuItem key={typeOption.value} value={typeOption.value}>
                {typeOption.label}
              </MenuItem>
            ))}
          </TextField>
          <CurrencyInput
            label="Valor"
            variant="outlined"
            margin="normal"
            fullWidth
            required
            value={amount}
            onChange={setAmount}
            currency={currency}
          />
          <TextField
            label="Observação"
            variant="outlined"
            margin="normal"
            fullWidth
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
          />

          {/* Consolidated Expense Section */}
          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isConsolidated}
                  onChange={(e) => {
                    setIsConsolidated(e.target.checked);
                    if (e.target.checked && childExpenses.length === 0) {
                      addChildExpense();
                    }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ReceiptLongIcon sx={{ fontSize: 18 }} />
                  <Typography variant="body2">Despesa consolidada (ex: fatura do cartão)</Typography>
                </Box>
              }
            />

            {isConsolidated && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Adicione as despesas que compõem esta fatura (ex: Seguro, Faculdade, etc.)
                </Typography>

                {childExpenses.map((child, index) => (
                  <Paper
                    key={child.id}
                    variant="outlined"
                    sx={{ p: 2, mb: 2, backgroundColor: 'action.hover' }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Despesa {index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeChildExpense(child.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <TextField
                      label="Descrição"
                      variant="outlined"
                      size="small"
                      fullWidth
                      required
                      value={child.description}
                      onChange={(e) => updateChildExpense(child.id, 'description', e.target.value)}
                      sx={{ mb: 1.5 }}
                    />

                    <Grid container spacing={1.5}>
                      <Grid item xs={7}>
                        <Autocomplete
                          options={categories}
                          getOptionLabel={(option) => option.Name}
                          value={child.category}
                          onChange={(e, newValue) => updateChildExpense(child.id, 'category', newValue)}
                          size="small"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Categoria"
                              variant="outlined"
                              required
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <CurrencyInput
                          label="Valor"
                          variant="outlined"
                          size="small"
                          fullWidth
                          required
                          value={child.value}
                          onChange={(val) => updateChildExpense(child.id, 'value', val)}
                          currency={currency}
                        />
                      </Grid>
                    </Grid>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={child.isMandatory}
                          onChange={(e) => updateChildExpense(child.id, 'isMandatory', e.target.checked)}
                          size="small"
                        />
                      }
                      label="Obrigatório"
                      sx={{ mt: 1 }}
                    />
                  </Paper>
                ))}

                <Button
                  startIcon={<AddIcon />}
                  onClick={addChildExpense}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  Adicionar despesa
                </Button>

                {childExpenses.length > 0 && (
                  <Alert
                    severity={getChildrenTotal() > amount ? 'warning' : 'info'}
                    sx={{ mt: 1 }}
                  >
                    Total detalhado: {formatValue(getChildrenTotal(), currency)}
                    {getChildrenTotal() > amount && ' (excede o valor principal!)'}
                    {getChildrenTotal() < amount && ` (${formatValue(amount - getChildrenTotal(), currency)} não detalhado)`}
                  </Alert>
                )}
              </Box>
            )}
          </Box>

          <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
            <Grid item>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={submitting}
              >
                {submitting ? 'Cadastrando...' : 'Cadastrar'}
              </Button>
            </Grid>
            <Grid item>
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                size="large"
                disabled={submitting}
                onClick={() => {
                  setDescription('');
                  setSelectedCategory(null);
                  setCurrency('BRL');
                  setAmount(0);
                  setObservation('');
                  setIsMandatory(false);
                  setDebitDate(dayjs().locale('pt-br').format('DD/MM/YYYY'));
                  setIsConsolidated(false);
                  setChildExpenses([]);
                }}
              >
                Limpar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Container>
  );
};

export default CashOutForm;